import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createApi } from "@reduxjs/toolkit/query/react";
import { apiUrls } from "constants/apiUrls";
import { ContactDetails } from "pages/Profile/EditProfile/ContactDetails/types";
import { Language } from "pages/Profile/EditProfile/LocationAndLanguages/types";
import { InterestResponse } from "pages/Profile/EditProfile/OtherInterests/types";
import {
    SocialResponse,
    UserResponse,
} from "pages/Profile/EditProfile/PersonalDetails/types";
import { PhysicalDetails } from "pages/Profile/EditProfile/PhysicalDetails/types";
import { Agency } from "pages/Profile/types";
import { UserResponse as UserSettingsResponse } from "pages/Settings/types";
import { baseQueryWithReAuth } from "utils/baseQueryWithReAuth";
import { getResolvedUrl } from "utils/getResolvedUrl";
import {
    ProfileStrengthResponse,
    updateAgencyLogoProps,
    UpdateAgencyProfileProps,
    User,
    UserLanguagesResponse,
} from "./types";
export const initialState: User = {
    id: "",
    email: "",
    first_name: "",
    is_agency: false,
    last_name: "",
    gender: "",
    nationality: "",
    dob: "",
    qualification: "",
    user_languages: [],
    user_location: undefined,
    experiences: [],
    is_verified: false,
};

export const userSlice = createSlice({
    name: "user",
    initialState,
    reducers: {
        setUser(state, { payload }: PayloadAction<User>) {
            localStorage.setItem("userInfo", JSON.stringify({ ...payload }));
            return {
                ...state,
                ...payload,
            };
        },
        setIsAgency(state, { payload }: PayloadAction<boolean>) {
            return {
                ...state,
                is_agency: payload,
            };
        },
        clearUserState(state) {
            localStorage.clear();
            state.id = initialState.id;
            state.email = initialState.email;
            state.first_name = initialState.first_name;
            state.last_name = initialState.last_name;
            state.gender = initialState.gender;
            state.nationality = initialState.nationality;
            state.dob = initialState.dob;
            state.qualification = initialState.qualification;
            state.user_languages = initialState.user_languages;
            state.user_location = initialState.user_location;
            state.experiences = initialState.experiences;
            state.is_agency = initialState.is_agency;
            state.is_verified = initialState.is_verified;
        },
    },
});

export const userApiSlice = createApi({
    reducerPath: "userApi",
    baseQuery: baseQueryWithReAuth,
    endpoints(builder) {
        return {
            getUser: builder.query<UserResponse, string>({
                query: (user_id) => ({
                    url: getResolvedUrl(apiUrls.users, { user_id }),
                }),
            }),
            getAgency: builder.query<UserResponse, string>({
                query: (agency_id) => ({
                    url: getResolvedUrl(apiUrls.getAgencyDetails, {
                        agency_id,
                    }),
                }),
            }),
            setContactDetails: builder.mutation({
                query: ({ method, user_id, ...rest }) => ({
                    method,
                    body: rest,
                    url: getResolvedUrl(apiUrls.contactDetails, { user_id }),
                }),
            }),
            getContactDetails: builder.query<ContactDetails, number | string>({
                query: (user_id) => ({
                    method: "GET",
                    url: getResolvedUrl(apiUrls.contactDetails, { user_id }),
                }),
            }),
            deleteContactDetails: builder.mutation({
                query: () => ({
                    method: "DELETE",
                    url: apiUrls.contactDetails,
                }),
            }),
            setPersonalDetails: builder.mutation({
                query: ({ user_id, method, ...body }) => ({
                    body,
                    method,
                    url: getResolvedUrl(apiUrls.users, { user_id }),
                }),
            }),
            getExperiences: builder.query({
                query: (payload) => ({
                    url: getResolvedUrl(apiUrls.experiences, {
                        user_id: payload.user_id,
                    }),
                    method: "GET",
                }),
            }),
            addEditExperiences: builder.mutation({
                query: (payload) => ({
                    url: getResolvedUrl(apiUrls.experiences, {
                        user_id: payload.user_id,
                    }),
                    method: payload.method,
                    body: payload.body,
                }),
            }),
            deleteExperience: builder.mutation({
                query: (payload) => ({
                    url: getResolvedUrl(apiUrls.modifyExperiences, {
                        user_id: payload.userId,
                        exp_id: payload.expId,
                    }),
                    method: "DELETE",
                }),
            }),
            getInterest: builder.query<InterestResponse, number | string>({
                query: (user_id) => ({
                    url: getResolvedUrl(apiUrls.interests, {
                        user_id,
                    }),
                }),
            }),
            setInterest: builder.mutation({
                query: ({ method, user_id, ...rest }) => ({
                    method,
                    body: rest,
                    url: getResolvedUrl(apiUrls.interests, { user_id }),
                }),
            }),
            getSocialLinks: builder.query<SocialResponse[], number | string>({
                query: (user_id) => ({
                    url: getResolvedUrl(apiUrls.socialLinks, {
                        user_id,
                    }),
                }),
            }),
            setSocialLinks: builder.mutation({
                query: ({ method, user_id, socialHandles }) => ({
                    method,
                    body: socialHandles.map((socialHandle: SocialResponse) => ({
                        username: socialHandle.username,
                        social_platforms_id: socialHandle.social_platforms_id,
                    })),
                    url: getResolvedUrl(apiUrls.socialLinks, { user_id }),
                }),
            }),
            getLanguages: builder.query<Language[], string>({
                query: (user_id) => ({
                    url: getResolvedUrl(apiUrls.languages, {
                        user_id,
                    }),
                }),
            }),
            getUserLanguages: builder.query<UserLanguagesResponse[], any>({
                query: (user_id) => ({
                    url: getResolvedUrl(apiUrls.userLanguages, {
                        user_id,
                    }),
                }),
            }),
            deleteUserLanguage: builder.mutation({
                query: ({ user_id, user_language_id }) => ({
                    url: getResolvedUrl(apiUrls.deleteUserLanguages, {
                        user_id,
                        user_language_id,
                    }),
                    method: "DELETE",
                }),
            }),
            addEditLanguages: builder.mutation({
                query: ({ method, user_id, data }) => ({
                    method,
                    body: data,
                    url: getResolvedUrl(apiUrls.userLanguages, { user_id }),
                }),
            }),
            getPhysicalDetails: builder.query<PhysicalDetails, number | string>(
                {
                    query: (user_id) => ({
                        url: apiUrls.physicalDetails,
                        params: {
                            user_id,
                        },
                    }),
                }
            ),
            setPhysicalDetails: builder.mutation({
                query: ({ method, url, ...body }) => ({
                    method,
                    body,
                    url,
                }),
            }),
            setUserPhotos: builder.mutation({
                query: ({ method, body, user_id }) => ({
                    method,
                    body,
                    url: getResolvedUrl(apiUrls.getOrPostImages, { user_id }),
                }),
            }),
            getUserPhotos: builder.query({
                query: (user_id) => ({
                    url: getResolvedUrl(apiUrls.getOrPostImages, { user_id }),
                }),
            }),
            putOrDeleteUserPhoto: builder.mutation({
                query: ({ image_id, body = {}, user_id, put = false }) => {
                    if (put) {
                        return {
                            method: "PUT",
                            body: body,
                            url: getResolvedUrl(apiUrls.getOrDeleteImage, {
                                image_id,
                                user_id,
                            }),
                        };
                    } else {
                        return {
                            method: "DELETE",
                            url: getResolvedUrl(apiUrls.getOrDeleteImage, {
                                image_id,
                                user_id,
                            }),
                        };
                    }
                },
            }),
            getUserProfileData: builder.query({
                query: ({ user_id }) => ({
                    url: getResolvedUrl(apiUrls.profile, { user_id }),
                }),
            }),
            getAgencyData: builder.query<Agency, { user_id: string }>({
                query: ({ user_id }) => ({
                    url: getResolvedUrl(apiUrls.agency, { user_id }),
                }),
            }),
            updateAgencyProfileData: builder.mutation({
                query: ({ data, user_id }: UpdateAgencyProfileProps) => ({
                    url: getResolvedUrl(apiUrls.updateProfile, { user_id }),
                    body: data,
                    method: "PUT",
                }),
            }),
            updateAgencyLogo: builder.mutation({
                query: ({ data, user_id }: updateAgencyLogoProps) => ({
                    url: getResolvedUrl(apiUrls.updateProfile, { user_id }),
                    body: data,
                    method: "PUT",
                }),
            }),
            getProfileStrength: builder.query<ProfileStrengthResponse, string>({
                query: (user_id) => ({
                    url: getResolvedUrl(apiUrls.profileStrength, { user_id }),
                }),
            }),
            getUserLocation: builder.query({
                query: (user_id) => ({
                    url: getResolvedUrl(apiUrls.userLocation, { user_id }),
                }),
            }),
            setUserLocation: builder.mutation({
                query: ({ user_id, method = "POST", data }) => ({
                    method,
                    body: data,
                    url: getResolvedUrl(apiUrls.userLocation, { user_id }),
                }),
            }),
            getModelInformation: builder.query<UserSettingsResponse, string>({
                query: (model_id) => ({
                    url: getResolvedUrl(apiUrls.profile, { user_id: model_id }),
                }),
            }),
        };
    },
});

export const {
    useGetUserQuery,
    useGetAgencyQuery,
    useSetContactDetailsMutation,
    useGetContactDetailsQuery,
    useDeleteContactDetailsMutation,
    useSetPersonalDetailsMutation,
    useAddEditExperiencesMutation,
    useGetExperiencesQuery,
    useDeleteExperienceMutation,
    useSetInterestMutation,
    useGetInterestQuery,
    useGetSocialLinksQuery,
    useSetSocialLinksMutation,
    useAddEditLanguagesMutation,
    useGetUserLanguagesQuery,
    useDeleteUserLanguageMutation,
    useGetPhysicalDetailsQuery,
    useSetPhysicalDetailsMutation,
    useSetUserPhotosMutation,
    useGetUserPhotosQuery,
    useGetUserProfileDataQuery,
    usePutOrDeleteUserPhotoMutation,
    useGetUserLocationQuery,
    useSetUserLocationMutation,
    useGetProfileStrengthQuery,
    useUpdateAgencyProfileDataMutation,
    useUpdateAgencyLogoMutation,
    useGetAgencyDataQuery,
    useGetModelInformationQuery,
} = userApiSlice;
export const { setUser, clearUserState, setIsAgency } = userSlice.actions;
export default userSlice.reducer;
